<script setup lang="ts">
import { computed, useTemplateRef, type Ref } from 'vue'
import { useHotkey } from 'vuetify'
import { useKeyModifier } from '@vueuse/core'

interface HotkeyOptions {
  inputs?: boolean
}

const props = withDefaults(
  defineProps<{
    keys: string
    hotkeyOptions?: HotkeyOptions
    tooltipLocation?: 'top' | 'bottom' | 'start' | 'end' | 'left' | 'right' | 'center'
    disabled?: boolean
    noTooltip?: boolean
    /** Only show the tooltip, do not register a hotkey handler. Must be set at mount time — not reactive. */
    tooltipOnly?: boolean
    clickActivator?: boolean
    preventDefault?: boolean
  }>(),
  {
    hotkeyOptions: undefined,
    tooltipLocation: 'bottom',
    disabled: false,
    noTooltip: false,
    tooltipOnly: false,
    clickActivator: false,
    preventDefault: true,
  }
)

const emit = defineEmits<{
  trigger: [event: KeyboardEvent]
}>()

const modifierRefs: Record<string, Ref<boolean | null>> = {
  alt: useKeyModifier('Alt'),
  ctrl: useKeyModifier('Control'),
  shift: useKeyModifier('Shift'),
  meta: useKeyModifier('Meta'),
}

const primaryModifierActive = computed(() => {
  if (props.disabled) return false
  const parts = props.keys.toLowerCase().split('+')
  for (const part of parts) {
    if (part in modifierRefs) {
      return modifierRefs[part]?.value === true
    }
  }
  return false
})

const anchorRef = useTemplateRef('anchorRef')

if (!props.tooltipOnly) {
  useHotkey(
    props.keys,
    (evt: KeyboardEvent) => {
      if (props.disabled) return
      if (props.preventDefault) {
        evt.preventDefault()
      }
      if (props.clickActivator) {
        anchorRef.value?.parentElement?.click()
      }
      emit('trigger', evt)
    },
    props.hotkeyOptions ?? {}
  )
}
</script>

<template>
  <span
    ref="anchorRef"
    aria-hidden="true"
    style="display: none"
  />
  <v-tooltip
    v-if="!noTooltip"
    activator="parent"
    :location="tooltipLocation"
    :model-value="primaryModifierActive || undefined"
  >
    <v-hotkey
      inline
      variant="text"
      color="surface"
      :keys="keys"
    />
  </v-tooltip>
</template>
